import { useEffect, useState } from "react";
import {
  Input,
  Button,
  FormField,
  Select,
  SpaceBetween,
  Form,
  Container,
  ColumnLayout,
  DatePicker,
  Box,
  RadioGroup,
  Grid,
} from "@cloudscape-design/components-themed";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../app/hook";
import { isLengthValid, isHalfNumberValid } from "../../utils/inputValidation";
import { getMessage } from "../../features/error/messages";
import { createSearchCondition } from "../../features/slices/rate-adjustment-case-search";
import { DivisionDict, COMMON_LABELS } from "../default-constants";
import { formatDate } from "../../utils/format";
import {
  LABELS,
  SELECT_BLANK,
  getIndustryConversionsItems,
  getDivisionItems,
} from "./constants";
import { ComprehensiveCompanySearchRequest } from "../../features/api-interfaces";
import {
  useGetDivisionQuery,
  useGetCommercialIndustriesQuery,
  useGetComprehensiveCompaniesQuery,
} from "../../features/api";
import { getComprehensiveMerchantItems } from "../../utils/common";

const addBlank = (list: DivisionDict[]) => {
  if (list) list.unshift(SELECT_BLANK as DivisionDict);
  return list;
};

const SearchBar = () => {
  // 法人番号
  const [corporateNumber, setCorporateNumber] = useState("");
  const [corporateNumberErrorMsg, setCorporateNumberErrorMsg] = useState("");

  // 事業者名
  const [companyName, setCompanyName] = useState("");
  const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState("");

  // 加盟店名
  const [merchantName, setMerchantName] = useState("");
  const [merchantNameErrorMsg, setMerchantNameErrorMsg] = useState("");

  // サービス名
  const [serviceName, setServiceName] = useState({ value: "" });

  // サービス名一覧
  const [serviceNameList, setServiceNameList] = useState<DivisionDict[]>();

  // 業態
  const [industry, setIndustry] = useState({ value: "" });

  // 取扱商材
  const [merchandise, setMerchandise] = useState({ value: "" });

  // 契約ルート
  const [contractRoute, setContractRoute] = useState("");

  // 包括加盟店
  const [comprehensiveMerchant, setComprehensiveMerchant] = useState({
    value: "",
  });

  // 包括加盟店一覧
  const [comprehensiveMerchantList, setComprehensiveMerchantList] =
    useState<DivisionDict[]>();

  // 回答日(開始)
  const [responseDateStart, setResponseDateStart] = useState("");

  // 回答日(終了)
  const [responseDateEnd, setResponseDateEnd] = useState("");

  // 売上高(開始)
  const [salesStart, setSalesStart] = useState("");
  const [salesStartErrorMsg, setSalesStartErrorMsg] = useState("");

  // 売上高(終了)
  const [salesEnd, setSalesEnd] = useState("");
  const [salesEndErrorMsg, setSalesEndErrorMsg] = useState("");

  // エラーメッセージ
  const [errText, setErrText] = useState<string>();

  // 変換後業態コード一覧
  const [afterConversionList, setAfterConversionList] =
    useState<DivisionDict[]>();

  // 商用反映済みの業態情報を取得する
  const { data: afterConversionItems } = useGetCommercialIndustriesQuery();

  useEffect(() => {
    setAfterConversionList(
      addBlank(getIndustryConversionsItems(afterConversionItems))
    );
  }, [afterConversionItems]);

  const navigate = useNavigate();

  // 共通データを取得
  // 区分取得API呼び出し(親区分コード：3:契約サービス区分コード)
  const { data: contractServiceKbnItems } = useGetDivisionQuery("3");

  useEffect(() => {
    setServiceNameList(addBlank(getDivisionItems(contractServiceKbnItems)));
  }, [contractServiceKbnItems]);

  // 包括加盟店データを取得
  const comprehensiveCompanySearchRequest = {
    companyCode: "",
    invisibleComprehensiveCompanyNoList: [],
  } as ComprehensiveCompanySearchRequest;

  const { data: comprehensiveMerchantListItems } =
    useGetComprehensiveCompaniesQuery(comprehensiveCompanySearchRequest);

  useEffect(() => {
    setComprehensiveMerchantList(
      addBlank(getComprehensiveMerchantItems(comprehensiveMerchantListItems))
    );
  }, [comprehensiveMerchantListItems]);

  const dispatch = useAppDispatch();

  // 検索ボタンイベント
  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // イベントキャンセル
    e.preventDefault();

    // Validate
    if (!checkCorporateNumber(corporateNumber)) return;
    if (!checkCompanyName(companyName)) return;
    if (!checkMerchantName(merchantName)) return;
    if (!checkSales(salesStart, true)) return;
    if (!checkSales(salesEnd, false)) return;

    dispatch(
      createSearchCondition({
        corporateNumber: corporateNumber,
        corporateName: companyName,
        merchantName: merchantName,
        contractRoute: Number(contractRoute),
        includeMerchantCompany: comprehensiveMerchant.value,
        serviceName: serviceName.value,
        industry: industry.value,
        handlingProducts: merchandise.value,
        responseDateStart: formatDate(responseDateStart),
        responseDateEnd: formatDate(responseDateEnd),
        salesStart: Number(salesStart),
        salesEnd: Number(salesEnd),
        timestamp: new Date().getTime(),
      })
    );
    // エラーメッセージクリア
    setErrText("");
  };

  // 法人番号バリデーションチェック
  const checkCorporateNumber = (corporateNumber: string): boolean => {
    if (!isHalfNumberValid(corporateNumber)) {
      setCorporateNumberErrorMsg(getMessage("KGRERR006").label);
      return false;
    } else if (!isLengthValid(corporateNumber, 13)) {
      setCorporateNumberErrorMsg(getMessage("KGRERR059", 13).label);
      return false;
    } else {
      setCorporateNumberErrorMsg("");
      return true;
    }
  };

  // 事業者名バリデーションチェック
  const checkCompanyName = (companyName: string): boolean => {
    if (!isLengthValid(companyName, 30)) {
      setCompanyNameErrorMsg(getMessage("KGRERR058", 30).label);
      return false;
    } else {
      setCompanyNameErrorMsg("");
      return true;
    }
  };

  // 加盟店名バリデーションチェック
  const checkMerchantName = (merchantName: string): boolean => {
    if (!isLengthValid(merchantName, 100)) {
      setMerchantNameErrorMsg(getMessage("KGRERR058", 100).label);
      return false;
    } else {
      setMerchantNameErrorMsg("");
      return true;
    }
  };

  // 売上高バリデーションチェック
  const checkSales = (sales: string, startFlag: boolean): boolean => {
    const setErrorMessage = startFlag
      ? setSalesStartErrorMsg
      : setSalesEndErrorMsg;
    if (!isHalfNumberValid(sales)) {
      setErrorMessage(getMessage("KGRERR006").label);
      return false;
    } else if (!isLengthValid(sales, 10)) {
      setErrorMessage(getMessage("KGRERR059", 10).label);
      return false;
    } else {
      setErrorMessage("");
      return true;
    }
  };

  return (
    <form onSubmit={handleOnSubmit}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="normal">{COMMON_LABELS.BTNS.SEARCH}</Button>
          </SpaceBetween>
        }
        errorText={errText}
      >
        <Container
          header={
            <Button
              onClick={() => {
                navigate("/rate-adjustment-case/register");
              }}
              formAction="none"
            >
              {LABELS.BTN_REGISTER}
            </Button>
          }
        >
          <SpaceBetween direction="vertical" size="l">
            <ColumnLayout columns={4}>
              <FormField
                label={LABELS.CORPORATE_NUMBER}
                errorText={corporateNumberErrorMsg}
                constraintText="半角数字13桁以内"
              >
                <Input
                  name="corporateNumber"
                  value={corporateNumber}
                  onChange={(e) => {
                    setCorporateNumber(e.detail.value);
                    checkCorporateNumber(e.detail.value);
                  }}
                />
              </FormField>
              <FormField
                label={LABELS.COMPANY_NAME}
                errorText={companyNameErrorMsg}
                constraintText="30文字以内"
              >
                <Input
                  name="companyName"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.detail.value);
                    checkCompanyName(e.detail.value);
                  }}
                />
              </FormField>
              <FormField
                label={LABELS.MERCHANT_NAME}
                errorText={merchantNameErrorMsg}
                constraintText="100文字以内"
              >
                <Input
                  name="merchantName"
                  value={merchantName}
                  onChange={(e) => {
                    setMerchantName(e.detail.value);
                    checkMerchantName(e.detail.value);
                  }}
                />
              </FormField>
              <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
                <FormField label={LABELS.CONTRACT_ROUTE}>
                  <RadioGroup
                    items={[
                      {
                        value: "",
                        label: LABELS.UN_SELECT,
                      },
                      {
                        value: "1",
                        label: LABELS.DIRECT,
                      },
                      {
                        value: "2",
                        label: LABELS.INCLUSION,
                      },
                    ]}
                    onChange={({ detail }) => {
                      setContractRoute(detail.value);
                      setComprehensiveMerchant({ value: "" });
                    }}
                    value={contractRoute}
                  />
                </FormField>
                <FormField
                  label={LABELS.EMPTY}
                  constraintText="　　　　　　　　　　　　　"
                >
                  <Select
                    disabled={contractRoute !== "2"}
                    selectedOption={comprehensiveMerchant}
                    options={comprehensiveMerchantList}
                    filteringType="auto"
                    onChange={(event: any) => {
                      setComprehensiveMerchant(event.detail.selectedOption);
                    }}
                  />
                </FormField>
              </Grid>
            </ColumnLayout>
            <ColumnLayout columns={4}>
              <FormField label={LABELS.SERVICE_NAME}>
                <Select
                  selectedOption={serviceName}
                  options={serviceNameList}
                  filteringType="none"
                  onChange={(event: any) => {
                    setServiceName(event.detail.selectedOption);
                    setIndustry({ value: "" });
                  }}
                />
              </FormField>
              <FormField label={LABELS.INDUSTRY}>
                <Select
                  disabled={serviceName.value !== LABELS.SERVICE_NAME_BARCODE}
                  selectedOption={industry}
                  options={afterConversionList}
                  filteringType="auto"
                  onChange={(event: any) => {
                    setIndustry(event.detail.selectedOption);
                  }}
                />
              </FormField>
              {/* Step1では項目の枠のみ用意のため仮データを定義 */}
              <FormField label={LABELS.MERCHANDISE}>
                <Select
                  disabled={serviceName.value !== "2"}
                  selectedOption={merchandise}
                  options={[{ label: "取扱商材1", value: "1" }]}
                  filteringType="none"
                  onChange={(event: any) => {
                    setMerchandise(event.detail.selectedOption);
                  }}
                />
              </FormField>
            </ColumnLayout>
            <SpaceBetween direction="horizontal" size="xs">
              <FormField label={LABELS.RESPONSE_DATE}>
                <DatePicker
                  onChange={({ detail }) => setResponseDateStart(detail.value)}
                  value={responseDateStart}
                  openCalendarAriaLabel={(selectedDate) =>
                    "Choose certificate expiry date" +
                    (selectedDate ? `, selected date is ${selectedDate}` : "")
                  }
                  nextMonthAriaLabel="Next month"
                  placeholder="YYYY/MM/DD"
                  previousMonthAriaLabel="Previous month"
                  todayAriaLabel="Today"
                />
              </FormField>
              <Box padding={{ top: "xxl" }} variant="awsui-key-label">
                {LABELS.FROM}
              </Box>
              <FormField label={LABELS.EMPTY}>
                <DatePicker
                  onChange={({ detail }) => setResponseDateEnd(detail.value)}
                  value={responseDateEnd}
                  openCalendarAriaLabel={(selectedDate) =>
                    "Choose certificate expiry date" +
                    (selectedDate ? `, selected date is ${selectedDate}` : "")
                  }
                  nextMonthAriaLabel="Next month"
                  placeholder="YYYY/MM/DD"
                  previousMonthAriaLabel="Previous month"
                  todayAriaLabel="Today"
                />
              </FormField>
            </SpaceBetween>
            <SpaceBetween direction="horizontal" size="xs">
              <FormField
                label={LABELS.SALES}
                errorText={salesStartErrorMsg}
                constraintText="半角数字10桁以内"
              >
                <Input
                  name="salesStart"
                  value={salesStart}
                  onChange={(e) => {
                    setSalesStart(e.detail.value);
                    checkSales(e.detail.value, true);
                  }}
                />
              </FormField>
              <Box padding={{ top: "xxl" }} variant="awsui-key-label">
                {LABELS.FROM}
              </Box>
              <FormField
                label={LABELS.EMPTY}
                errorText={salesEndErrorMsg}
                constraintText="半角数字10桁以内"
              >
                <Input
                  name="salesEnd"
                  value={salesEnd}
                  onChange={(e) => {
                    setSalesEnd(e.detail.value);
                    checkSales(e.detail.value, false);
                  }}
                />
              </FormField>
              <Box padding={{ top: "xxl" }} variant="awsui-key-label">
                {LABELS.BILLION_YEN}
              </Box>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      </Form>
    </form>
  );
};

export default SearchBar;
